import { useEffect, useState } from "react";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import {
  DropDownList,
  DropDownListChangeEvent,
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { SiGoogleclassroom } from "react-icons/si";
import { defaultProgramYear } from "@/utils/dates";
import { accessibleNoDataRender } from "@/components/shared/forms/kendoAccessibleNoData";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

export type ClassroomSearchFormState = {
  programYear: string;
  schools: PreschoolSummary[];
  classroomName: string;
  classroomId: string;
};

type ClassroomSearchFormProps = {
  programYearOptions: string[];
  preschools: PreschoolSummary[];
  disabled: boolean;
  onSearch: (filters: ClassroomSearchFormState) => void;
  initialProgramYear?: string;
  initialClassroomId?: string;
  onClear?: () => void;
};

export function ClassroomSearchForm({
  programYearOptions,
  preschools,
  disabled,
  onSearch,
  initialProgramYear,
  initialClassroomId,
  onClear,
}: ClassroomSearchFormProps) {
  const [formState, setFormState] = useState<ClassroomSearchFormState>(() => ({
    programYear: defaultProgramYear(initialProgramYear),
    schools: [],
    classroomName: "",
    classroomId: initialClassroomId ?? "",
  }));

  // See MultiSelectToggleButton: gives keyboard users a way to browse the
  // School list without having to type first.
  const [schoolsOpened, setSchoolsOpened] = useState(false);

  useMultiSelectChipListLabel(
    "classroom-search-school",
    "School",
    formState.schools
  );
  useMultiSelectListboxMultiselectable(
    "classroom-search-school",
    schoolsOpened
  );

  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      programYear: defaultProgramYear(initialProgramYear),
    }));
  }, [initialProgramYear]);

  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      classroomId: initialClassroomId ?? "",
    }));
  }, [initialClassroomId]);
  const handleChange = (key: keyof ClassroomSearchFormState, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSearchClick = () => {
    if (!disabled) onSearch(formState);
  };

  const handleClearClick = () => {
    const cleared: ClassroomSearchFormState = {
      programYear: defaultProgramYear(),
      schools: [],
      classroomName: "",
      classroomId: "",
    };

    setFormState(cleared);

    if (!disabled) onSearch(cleared);

    onClear?.();
  };

  return (
    <div className="mb-0 p-4 bg-white w-100 child-search-form">
      <div className="row g-3">
        {/* Program Year */}
        <div className="col-12 col-md-6">
          <span
            id="classroom-search-program-year-label"
            className="form-label fw-semibold"
          >
            <i className="bi bi-calendar me-1" aria-hidden="true"></i> Program
            Year
          </span>
          <DropDownList
            id="classroom-search-program-year"
            ariaLabelledBy="classroom-search-program-year-label"
            className="child-search-control"
            style={{ width: "100%" }}
            data={programYearOptions}
            value={formState.programYear}
            onChange={(e: DropDownListChangeEvent) => {
              // Kendo's DropDownList can fire onChange with an empty/undefined
              // value during keyboard navigation (arrowing through the open
              // popup), before it settles on the actually-highlighted item.
              // Writing that straight into state blanks the displayed value
              // and it stays blank, since nothing re-derives it afterward.
              if (e.value) handleChange("programYear", e.value);
            }}
            disabled={disabled}
            listNoDataRender={accessibleNoDataRender(
              "No program years available"
            )}
          />
        </div>

        {/* School (MultiSelect) */}
        <div className="col-12 col-md-6">
          <label
            id="classroom-search-school-label"
            htmlFor="classroom-search-school"
            className="form-label fw-semibold"
          >
            <i className="bi bi-building me-1" aria-hidden="true"></i> School
          </label>
          <MultiSelect
            id="classroom-search-school"
            ariaLabelledBy="classroom-search-school-label"
            className="child-search-control"
            style={{ width: "100%" }}
            data={preschools}
            textField="PreschoolName"
            dataItemKey="PreschoolCDSCode"
            value={formState.schools}
            onChange={(e: MultiSelectChangeEvent) =>
              handleChange("schools", e.value as PreschoolSummary[])
            }
            placeholder="All schools"
            disabled={disabled}
            listNoDataRender={accessibleNoDataRender("No schools available")}
            opened={schoolsOpened}
            onOpen={() => setSchoolsOpened(true)}
            onClose={() => setSchoolsOpened(false)}
            itemRender={multiSelectSelectedItemRender}
            popupSettings={{
              className: "classroom-search-school-listbox-popup",
            }}
            tagRender={multiSelectTagRender}
            suffix={
              <>
                <MultiSelectToggleButton
                  opened={schoolsOpened}
                  onToggle={() => setSchoolsOpened((prev) => !prev)}
                  disabled={disabled}
                />
                {formState.schools.length > 0 && (
                  <MultiSelectClearButton
                    onClear={() => handleChange("schools", [])}
                    disabled={disabled}
                  />
                )}
              </>
            }
          />
        </div>

        {/* Classroom Name */}
        <div className="col-12 col-md-6">
          <label
            id="classroom-search-name-label"
            htmlFor="classroom-search-name"
            className="form-label fw-semibold"
          >
            <SiGoogleclassroom className="me-1" aria-hidden="true" /> Classroom
            Name
          </label>
          <Input
            id="classroom-search-name"
            ariaLabelledBy="classroom-search-name-label"
            className="child-search-control"
            style={{ width: "100%" }}
            value={formState.classroomName}
            onChange={(e: InputChangeEvent) =>
              handleChange("classroomName", e.value)
            }
            placeholder="Enter classroom name"
            disabled={disabled}
          />
        </div>

        {/* Classroom ID */}
        <div className="col-12 col-md-6">
          <label
            id="classroom-search-id-label"
            htmlFor="classroom-search-id"
            className="form-label fw-semibold"
          >
            <i className="bi bi-hash me-1" aria-hidden="true"></i> Classroom ID
          </label>
          <Input
            id="classroom-search-id"
            ariaLabelledBy="classroom-search-id-label"
            className="child-search-control"
            style={{ width: "100%" }}
            value={formState.classroomId}
            onChange={(e: InputChangeEvent) =>
              handleChange("classroomId", e.value)
            }
            placeholder="Enter classroom ID"
            disabled={disabled}
          />
        </div>

        {/* Buttons */}
        <div className="col-12 d-flex align-items-end gap-2 mt-4 mb-1">
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={handleSearchClick}
            disabled={disabled}
          >
            <i className="bi bi-search" aria-hidden="true"></i>
            Search
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClearClick}
            disabled={disabled}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
